import { useEffect, useState } from "react";
import { Vote, CheckCircle2, XCircle, Star, Crown, Trash2, FileText, Archive, Download, UserPlus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { formatDateTime } from "@/constants";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_ADD = { first_name: "", last_name: "", email: "", phone: "", address: "", child_class: "", consent_image: false, validate_now: true };

export default function AdminListeCetf() {
  const [candidacies, setCandidacies] = useState(null);
  const [archives, setArchives] = useState([]);
  const [motifTarget, setMotifTarget] = useState(null);
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [settings, setSettings] = useState(null);
  const [yearInput, setYearInput] = useState("");

  const load = () => {
    api.get("/admin/candidacies").then((r) => setCandidacies(r.data)).catch((e) => { toast.error(formatApiError(e)); setCandidacies([]); });
    api.get("/admin/candidacies-archive").then((r) => setArchives(r.data)).catch(() => setArchives([]));
    api.get("/admin/liste-cetf/settings").then((r) => { setSettings(r.data); setYearInput(r.data.next_school_year || ""); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = (open) => act(
    () => api.post("/admin/liste-cetf/settings", { candidacy_open: open, next_school_year: yearInput }),
    open ? "Dépôt de candidature activé." : "Dépôt de candidature clôturé."
  );

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const addSubmit = async (e) => {
    e.preventDefault();
    await act(async () => {
      const { validate_now, ...payload } = addForm;
      await api.post("/admin/candidacies", { ...payload, status: validate_now ? "approved" : "pending" });
    }, "Candidat ajouté.");
    setAddOpen(false);
    setAddForm(EMPTY_ADD);
  };

  const archive = async () => {
    if (!motifTarget || motif.trim().length < 2) return;
    await act(() => api.post(`/admin/candidacies/${motifTarget.id}/archive`, { motif: motif.trim() }), "Candidat retiré et archivé avec motif.");
    setMotifTarget(null);
    setMotif("");
  };

  const pending = (candidacies || []).filter((c) => c.status === "pending");
  const approved = (candidacies || []).filter((c) => c.status === "approved");
  const rejected = (candidacies || []).filter((c) => c.status === "rejected");

  return (
    <div className="space-y-8" data-testid="admin-liste-cetf-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
          <Vote className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-liste-cetf-title">Liste CETF</h1>
          <p className="text-sm text-slate-500 mt-0.5">Candidatures aux élections de parents d'élèves — validation, référente de liste, archives.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" asChild data-testid="export-association-btn">
            <a href={`${API_BASE}/admin/candidacies/export/association`} target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5 mr-1.5" />PDF association
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="export-ecole-btn">
            <a href={`${API_BASE}/admin/candidacies/export/ecole`} target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5 mr-1.5" />PDF école (signatures)
            </a>
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAddOpen(true)} data-testid="candidacy-add-open-btn">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />Ajouter un candidat
          </Button>
        </div>
      </div>

      <Card className={settings?.candidacy_open ? "border-emerald-300 bg-emerald-50/60" : "border-slate-300 bg-slate-50"} data-testid="candidacy-settings-card">
        <CardContent className="p-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="min-w-[240px] flex-1">
            <h2 className="text-base font-bold text-slate-900">Dépôt de candidature</h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Le calendrier électoral est fixé chaque année par la circulaire du ministère de l'Éducation nationale
              (publiée en juin–juillet) : dépôt fin septembre, élections début octobre. Vérifiez la circulaire de
              l'année en cours en juin/juillet/août avant de rouvrir le dépôt.
            </p>
          </div>
          {settings && !settings.candidacy_open && (
            <div className="space-y-1">
              <Label htmlFor="next-school-year" className="text-xs">Prochaine année scolaire affichée</Label>
              <Input id="next-school-year" className="w-36 h-9" maxLength={9} placeholder="2027-2028" data-testid="next-school-year-input"
                value={yearInput} onChange={(e) => setYearInput(e.target.value)} />
            </div>
          )}
          {settings && (
            <Badge className={settings.candidacy_open
              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
              : "bg-slate-200 text-slate-700 border border-slate-300"} data-testid="candidacy-status-badge">
              {settings.candidacy_open ? "Dépôt de candidature activé" : "Dépôt de candidature clôturé"}
            </Badge>
          )}
          {settings && (
            <Button size="sm" disabled={busy}
              className={settings.candidacy_open ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              onClick={() => saveSettings(!settings.candidacy_open)}
              data-testid="candidacy-toggle-btn">
              {settings.candidacy_open ? "Clôturer le dépôt" : "Activer le dépôt"}
            </Button>
          )}
        </CardContent>
      </Card>

      {candidacies === null ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : (
        <>
          <section data-testid="pending-section">
            <h2 className="text-lg font-bold text-slate-800 mb-3">En attente de validation ({pending.length})</h2>
            {pending.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-5">Aucune candidature en attente.</p>
            ) : (
              <div className="space-y-3">
                {pending.map((c) => (
                  <Card key={c.id} className="border-amber-200 bg-amber-50/50" data-testid={`candidacy-pending-${c.id}`}>
                    <CardContent className="p-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                      <div className="min-w-[220px] flex-1">
                        <p className="font-semibold text-slate-800">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-slate-500">{c.email} · {c.phone}{c.address ? ` · ${c.address}` : ""} · classe : {c.child_class}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Reçue le {formatDateTime(c.created_at)} · droit à l'image : {c.consent_image ? "oui" : "non"}
                        </p>
                      </div>
                      {c.has_id_document && (
                        <Button variant="outline" size="sm" asChild data-testid={`candidacy-doc-${c.id}`}>
                          <a href={`${API_BASE}/admin/candidacies/${c.id}/document`} target="_blank" rel="noopener noreferrer">
                            <FileText className="h-3.5 w-3.5 mr-1.5" />Pièce d'identité
                          </a>
                        </Button>
                      )}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy}
                        onClick={() => act(() => api.post(`/admin/candidacies/${c.id}/approve`), "Candidature approuvée — le candidat a été notifié par email.")}
                        data-testid={`candidacy-approve-${c.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approuver
                      </Button>
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => act(() => api.post(`/admin/candidacies/${c.id}/reject`), "Candidature refusée.")}
                        data-testid={`candidacy-reject-${c.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1.5" />Refuser
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          <section data-testid="approved-section">
            <h2 className="text-lg font-bold text-slate-800 mb-3">Candidats validés ({approved.length})</h2>
            {approved.length === 0 ? (
              <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-5">Aucun candidat validé pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {approved.map((c) => (
                  <Card key={c.id} className="border-slate-200" data-testid={`candidacy-approved-${c.id}`}>
                    <CardContent className="p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <div className="min-w-[220px] flex-1">
                        <p className="font-semibold text-slate-800">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-slate-500">{c.email} · {c.phone}{c.address ? ` · ${c.address}` : ""} · classe : {c.child_class}</p>
                        <p className="text-xs text-slate-400 mt-0.5">Charte « Soutenu par CETF » : {c.consent_soutien ? "cochée" : "pas encore cochée"}</p>
                      </div>
                      <Badge className={c.is_interim_referent
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                        : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100"}>
                        {c.is_interim_referent ? "Référente par intérim" : "Candidat·e"}
                      </Badge>
                      {c.is_list_lead && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">Chef·fe de liste</Badge>
                      )}
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => act(() => api.patch(`/admin/candidacies/${c.id}/flags`, { is_interim_referent: !c.is_interim_referent }),
                          c.is_interim_referent ? "Référence par intérim retirée." : "Référente de liste par intérim nommée.")}
                        data-testid={`candidacy-referent-${c.id}`}>
                        <Star className="h-3.5 w-3.5 mr-1.5" />{c.is_interim_referent ? "Retirer l'intérim" : "Nommer référent·e intérim"}
                      </Button>
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => act(() => api.patch(`/admin/candidacies/${c.id}/flags`, { is_list_lead: !c.is_list_lead }),
                          c.is_list_lead ? "Chef·fe de liste retiré·e." : "Chef·fe de liste nommé·e.")}
                        data-testid={`candidacy-lead-${c.id}`}>
                        <Crown className="h-3.5 w-3.5 mr-1.5" />{c.is_list_lead ? "Retirer chef·fe" : "Nommer chef·fe de liste"}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => { setMotifTarget(c); setMotif(""); }} data-testid={`candidacy-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {rejected.length > 0 && (
            <p className="text-xs text-slate-400">{rejected.length} candidature(s) refusée(s).</p>
          )}

          {archives.length > 0 && (
            <section data-testid="candidacies-archive-section">
              <h2 className="text-lg font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Archive className="h-5 w-5 text-slate-400" />Anciens candidats (super-admin & chef·fe de liste)
              </h2>
              <div className="space-y-2">
                {archives.map((a, i) => (
                  <div key={a.id || i} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-500" data-testid={`candidacy-archived-${i}`}>
                    <span className="font-semibold text-slate-600">{a.first_name} {a.last_name}</span> — retiré·e
                    le {formatDateTime(a.archived_at)} par {a.archived_by} · <span className="italic">motif : {a.motif}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Dialog open={!!motifTarget} onOpenChange={(o) => { if (!o) setMotifTarget(null); }}>
        <DialogContent data-testid="candidacy-archive-dialog">
          <DialogHeader>
            <DialogTitle>Retirer ce candidat de la liste</DialogTitle>
          </DialogHeader>
          {motifTarget && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <strong>{motifTarget.first_name} {motifTarget.last_name}</strong> sera retiré·e de la liste et archivé·e
                dans « Anciens candidats » avec le motif (démission, etc.).
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="candidacy-motif">Motif du retrait *</Label>
                <Input id="candidacy-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                  placeholder="Ex. : démission, déménagement…" data-testid="candidacy-motif-input" />
              </div>
              <div className="flex gap-2.5">
                <Button className="bg-red-600 hover:bg-red-700" onClick={archive}
                  disabled={busy || motif.trim().length < 2} data-testid="candidacy-archive-confirm-btn">
                  {busy ? "Suppression…" : "Retirer avec ce motif"}
                </Button>
                <Button variant="outline" onClick={() => setMotifTarget(null)}>Annuler</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="candidacy-add-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un candidat manuellement</DialogTitle>
          </DialogHeader>
          <form onSubmit={addSubmit} className="space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Pour les parents rencontrés en personne qui n'ont pas le temps de remplir le formulaire en ligne.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="add-firstname">Prénom *</Label>
                <Input id="add-firstname" required maxLength={80} data-testid="candidacy-add-firstname-input"
                  value={addForm.first_name} onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-lastname">Nom *</Label>
                <Input id="add-lastname" required maxLength={80} data-testid="candidacy-add-lastname-input"
                  value={addForm.last_name} onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="add-email">Email *</Label>
                <Input id="add-email" type="email" required data-testid="candidacy-add-email-input"
                  value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-phone">Téléphone *</Label>
                <Input id="add-phone" type="tel" required maxLength={30} data-testid="candidacy-add-phone-input"
                  value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-address">Adresse postale</Label>
              <Input id="add-address" maxLength={300} data-testid="candidacy-add-address-input"
                value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-class">Classe de l'enfant *</Label>
              <Input id="add-class" required maxLength={80} placeholder="Ex. : CE2 — Mme Dupont" data-testid="candidacy-add-class-input"
                value={addForm.child_class} onChange={(e) => setAddForm({ ...addForm, child_class: e.target.value })} />
            </div>
            <div className="space-y-2.5 bg-slate-50 rounded-xl p-3.5">
              <div className="flex items-start gap-2.5">
                <Checkbox id="add-validate" className="mt-0.5" data-testid="candidacy-add-validate-checkbox"
                  checked={addForm.validate_now} onCheckedChange={(v) => setAddForm({ ...addForm, validate_now: v === true })} />
                <Label htmlFor="add-validate" className="text-sm font-normal leading-snug text-slate-600 cursor-pointer">
                  Candidat directement validé (rencontré en personne — entretien déjà réalisé)
                </Label>
              </div>
              <div className="flex items-start gap-2.5">
                <Checkbox id="add-image" className="mt-0.5" data-testid="candidacy-add-image-checkbox"
                  checked={addForm.consent_image} onCheckedChange={(v) => setAddForm({ ...addForm, consent_image: v === true })} />
                <Label htmlFor="add-image" className="text-sm font-normal leading-snug text-slate-600 cursor-pointer">
                  Droit à l'image accepté
                </Label>
              </div>
            </div>
            <div className="flex gap-2.5">
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="candidacy-add-submit-btn">
                {busy ? "Ajout…" : "Ajouter le candidat"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
